import { enumLabel, formatDate, number, t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import type { Report } from '@repo/shared/schema';
import { isReportCompleted, RiskLevel } from '@repo/shared/schema';
import { dateLabel } from './shared';

const palette: Record<string, string> = {
  HIGH: '#b36183',
  MEDIUM: '#b89c5a',
  LOW: '#62b2a2',
  ACCEPTED: '#62b2a2',
  REJECTED: '#b36183',
  PENDING: '#c689d0',
};
export const MonthlyChart = ({
  rows,
  series,
}: {
  rows: { month: string; status: string; count: number }[];
  series: string[];
}) => {
  useLocale();

  const months = [...new Set(rows.map((row) => row.month))].sort();
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <div className="mt-6">
      <div
        aria-label={t('workspace.monthlyTotals')}
        className="flex h-64 items-end justify-around gap-4 border-b px-3"
        role="img"
      >
        {months.length ? (
          months.map((month) => (
            <div className="flex h-full flex-1 flex-col justify-end" key={month}>
              <div className="flex h-56 items-end justify-center gap-2">
                {series.map((status) => {
                  const value =
                    rows.find((row) => row.month === month && row.status === status)?.count ?? 0;
                  return (
                    <div
                      className="w-full max-w-8 rounded-t-sm"
                      key={status}
                      style={{
                        height: `${(value / max) * 90}%`,
                        minHeight: value ? 3 : 0,
                        background: palette[status],
                      }}
                      title={`${month}: ${status} ${value}`}
                    />
                  );
                })}
              </div>
              <span className="raven-muted py-3 text-center">
                {formatDate(`${month}-01T00:00:00Z`, getLocale(), {
                  calendar: 'gregory',
                  month: 'short',
                  timeZone: 'UTC',
                })}
              </span>
            </div>
          ))
        ) : (
          <p className="raven-muted m-auto">{t('workspace.noDataInThisPeriod')}</p>
        )}
      </div>
      <div className="mt-6 flex flex-wrap justify-center gap-5 text-xs">
        {series.map((status) => (
          <span className="flex items-center gap-2" key={status}>
            <i className="size-2 rounded-full" style={{ background: palette[status] }} />
            {enumLabel(status)}
          </span>
        ))}
      </div>
      <details className="mt-4 text-xs">
        <summary>{t('workspace.viewChartData')}</summary>
        <table className="raven-table">
          <tbody>
            {rows.map((row) => (
              <tr key={`${row.month}-${enumLabel(row.status)}`}>
                <td>{row.month}</td>
                <td>{enumLabel(row.status)}</td>
                <td>{number(row.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};

export const ReviewChart = ({
  reports,
  selectedId,
  onSelect,
}: {
  reports: Report[];
  selectedId: string;
  onSelect: (id: string) => void;
}) => {
  useLocale();

  const ordered = [...reports].reverse().filter((report) => isReportCompleted(report.status));
  const width = Math.max(650, ordered.length * 90);
  const y = (level?: string) =>
    level === 'HIGH' ? 40 : level === 'MEDIUM' ? 100 : level === 'LOW' ? 160 : 205;
  return (
    <div className="my-5 overflow-x-auto rounded-xl border p-4">
      <svg
        aria-label={t('workspace.reportRisksOverTimeSelectAPointToInspect')}
        className="min-w-[650px]"
        role="img"
        style={{ width }}
        viewBox={`0 0 ${width} 265`}
      >
        {[RiskLevel.HIGH, RiskLevel.MEDIUM, RiskLevel.LOW].map((level) => (
          <g key={level}>
            <text fill={palette[level]} fontSize="10" x="0" y={y(level) + 4}>
              {enumLabel(level)}
            </text>
            <line stroke="#edebf5" x1="85" x2={width - 15} y1={y(level)} y2={y(level)} />
          </g>
        ))}
        <polyline
          fill="none"
          points={ordered
            .map(
              (report, index) =>
                `${110 + (index * (width - 150)) / Math.max(1, ordered.length - 1)},${y(report.riskLevel)}`,
            )
            .join(' ')}
          stroke="#b6afc4"
          strokeWidth="2"
        />
        {ordered.map((report, index) => {
          const x = 110 + (index * (width - 150)) / Math.max(1, ordered.length - 1);
          return (
            <g
              aria-label={`Report ${index + 1}: ${report.riskLevel ?? RiskLevel.UNASSESSED}, ${dateLabel(report.createdAt)}`}
              key={report._id}
              onClick={() => onSelect(report._id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(report._id);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <circle
                cx={x}
                cy={y(report.riskLevel)}
                fill={palette[report.riskLevel ?? RiskLevel.UNASSESSED] ?? '#b6b3c6'}
                r={selectedId === report._id ? 8 : 5}
                stroke={selectedId === report._id ? '#e9e1cb' : 'white'}
                strokeWidth="3"
              />
              <text fill="#353d93" fontSize="10" textAnchor="middle" x={x} y="237">
                {t('workspace.report')} {index + 1}
              </text>
              <text fill="#8e8198" fontSize="8" textAnchor="middle" x={x} y="255">
                {dateLabel(report.createdAt)}
              </text>
            </g>
          );
        })}
        {!ordered.length && (
          <text fill="#8e8198" fontSize="12" x="220" y="120">
            {t('workspace.noGeneratedReportsYet')}{' '}
          </text>
        )}
      </svg>
    </div>
  );
};
